import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import "./ProcurementChat.css";
import { useNavigate } from "../../hooks/useRouter";
import { useDispatch } from "react-redux";
import axios from "axios";

// components
import ChatHeader from "../../components/ChatHeader/ChatHeader";
import ProcurementDynamicTable from "../../components/ProcurementDynamicTable/ProcurementDynamicTable";
import FeedbackButtons from "../../components/FeedbackButtons/FeedbackButtons";
import Dashboard from "../../components/Dashboard/Dashboard";
import ChatPromptContainer from "../../components/ChatPromptContainer/ChatPromptContainer";
import MessageBox from "../../components/MessageBox/MessageBox";
import MessageGraphs from "../../components/MessageGraphs/MessageGraphs";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import MarkdownContent from "../../components/MarkdownContent/MarkdownContent";

// store
import {
    fetchProcurementChatCompletions,
    createProcurementSession,
    getProcurementProgressTracker,
    getProcurementChartStatus,
    updateProcurementFeedback,
    fetchProcurementUserConversation,
    fetchProcurementTemplateRequest,
    fetchProcurementTemplateOptions,
} from "../../store/apiActions";

// utility & constants
import useIsMobile from "../../hooks/useIsMobile";
import { useEventEmitter } from "../../contexts/EventEmmiter";
import { generateCustomId, scrollToLatestViewPoint } from "../../utils/commonHelper";
import { cancelCancelToken, handleUnauthorized } from "../../utils/apiHelper";
import { formatMessageDate, getDateKey, convertGMTToIST, formatMessageTime } from "../../utils/dateUtils";
import { sessionManager } from "../../utils/utility";
import { useConstants } from "../../constant/constantProvider";
import { sanitizeTextInput, sanitizeTextPreservingHTML } from "../../utils/textUtils";
import { useAuth } from "../../hooks/useAuth";
import { formatProcurementGraphSnapshot } from "../../utils/graphUtils";

import { parseTableData, parseRelevantNames } from "../../utils/templateUtils";

// images
import downArrow from "../../assets/new-images/dropdown-icon.svg";
import procurementBotIcon from "../../assets/images/procurement.svg";
import bulbIcon from "../../assets/images/bulb.svg";
import insightIcon from "../../assets/images/insights.svg";
import creditIcon from "../../assets/new-images/credit.svg";

function ProcurementChat() {
    const { emit, subscribe } = useEventEmitter();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isMobile = useIsMobile();

    const {
        PROCUREMENT_AI,
        SHARADHA_AI,
        SHRADDHA_AI,
        UNDERSTANDING_THE_QUERY,
        QUERY_RESULT,
        FEEDBACK_SUBMIT_SUCCESS,
        SESSION_EXPIRED,
        YOU_ARE_CHATTING_WITH,
        BACK_TO_CHAT_GENIE,
    } = useConstants();

    const [message, setMessage] = useState("");
    const [isTextareaFocused, setIsTextareaFocused] = useState(false);
    const chatEndRef = useRef(null);
    const [converstation, setConverstation] = useState([]);
    const [uiConversation, setUIConversation] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedFeedback, setSelectedFeedback] = useState({});
    const [feedbackInput, setFeedbackInput] = useState({});

    const [submittingFeedback, setSubmittingFeedback] = useState(false);
    const [sessionId, setSessionId] = useState("");

    const [editingId, setEditingId] = useState(null);
    const [editedText, setEditedText] = useState("");
    const richInputRef = useRef(null);
    const streamReaderRef = useRef(null);
    const streamAbortControllerRef = useRef(null);
    const fileUploadCancelRef = useRef(false);
    const templateApiCancelTokenRef = useRef(null);
    const chatApiCancelTokenRef = useRef(null);
    const assistantRefs = useRef([]);
    const progressIntervalRef = useRef(null);

    const clearProgressInterval = () => {
        if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
        }
    };

    const [isTextBoxDisabled, setIsTextBoxDisabled] = useState(false);

    // eslint-disable-next-line
    const [isSendButtonDisabled, setIsSendButtonDisabled] = useState(true);

    const [isChatLoading, setIsChatLoading] = useState(false);

    const [procurementTablesData, setProcurementTablesData] = useState({});
    const [procurementChartsData, setProcurementChartsData] = useState({});
    const [isTemplateQueryMode, setIsTemplateQueryMode] = useState(false);
    const [selectedFollowUps, setSelectedFollowUps] = useState({});
    const [lastSpendSummaryTimestamp, setLastSpendSummaryTimestamp] = useState(null);

    // Template options fetched from API on initial load
    const [templateOptions, setTemplateOptions] = useState([]);
    const spendSummaryPrompts = templateOptions.map((opt) => opt.template_name);

    const [showScrollButton, setShowScrollButton] = useState(false);
    const chatContainerRef = useRef(null);

    const { username, fullName: fullname } = useAuth();

    const handleGraphActionStatus = (status) => {
        if (status === "copy-success") {
            toast.info("Graph copied to clipboard", { autoClose: 1000 });
        } else if (status === "copy-error") {
            toast.error("Failed to copy graph");
        } else if (status === "download-success") {
            toast.info("Graph downloaded successfully");
        } else if (status === "download-error") {
            toast.error("Unable to download this graph");
        } else if (status === "data-copy-success") {
            toast.info("Complete graph data copied as a table", { autoClose: 1000 });
        } else if (status === "data-copy-error") {
            toast.error("Failed to copy graph data");
        }
    };

    const procurementCardsData = [
        {
            icon: bulbIcon,
            title: "Instant Spend Insights",
            // subtitle: "Ask by material, plant, region, or company code.",
            prompts: [
                "Top 10 materials by spend in Plant PL01 in the last quarter",
                "Total spend for company code 1000 in the last year"
            ]
        },
        {
            icon: insightIcon,
            title: "Trend Analysis",
            // subtitle: "Track spend trends across categories and regions.",
            prompts: [
                "Price trends for top 3 materials in the last 3 years",
                "Spend trends for top 3 vendors in the last 3 years"
            ]
        },
        {
            icon: creditIcon,
            title: "Credit & SOB Analysis",
            // subtitle: "Get ranked lists and filtered views instantly.",
            prompts: [
                "Compare CNOD for top 10 vendors for the current and previous years",
                "Share change in SOB for top 10 materials compared to the last year"
            ]
        },
    ];

    useEffect(() => {
        const unsubscribe = subscribe("newProcurementChatDirect", () => {
            handleNewSessionContinue();
        });
        return () => unsubscribe();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const unsubscribe = subscribe("clearChat", () => {
            clearChatData();
        });
        return () => unsubscribe();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        handleNewSessionContinue();
        fetchTemplateOptions();
        return () => {
            clearProgressInterval();
            if (chatApiCancelTokenRef.current) {
                cancelCancelToken(chatApiCancelTokenRef.current, "Component unmounted", "procurement chat API");
                chatApiCancelTokenRef.current = null;
            }
            if (templateApiCancelTokenRef.current) {
                cancelCancelToken(templateApiCancelTokenRef.current, "Component unmounted", "template API");
                templateApiCancelTokenRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchTemplateOptions = async () => {
        try {
            const response = await dispatch(fetchProcurementTemplateOptions());
            if (response?.options && Array.isArray(response.options)) {
                setTemplateOptions(response.options);
            }
        } catch (error) {
            console.error("Failed to fetch template options:", error);
        }
    };

    const routeToUPLGenieChat = () => {
        navigate('/chat');
    };

    const clearChatData = () => {
        clearProgressInterval();
        setConverstation([]);
        setUIConversation([]);
        setSelectedFeedback({});
        setIsChatLoading(false);
        setProcurementTablesData({});
        setProcurementChartsData({});
        setIsTemplateQueryMode(false);
        setSelectedFollowUps({});
        sessionManager.setSessionId(null, "procAISessionId");
        setSessionId("");
        setMessage("");
        if (richInputRef.current) {
            richInputRef.current.clear();
        }
        if (templateApiCancelTokenRef.current) {
            cancelCancelToken(templateApiCancelTokenRef.current, "Template API cancelled due to new chat", "template API");
            templateApiCancelTokenRef.current = null;
        }
        if (chatApiCancelTokenRef.current) {
            cancelCancelToken(chatApiCancelTokenRef.current, "Chat API cancelled due to new chat", "procurement chat API");
            chatApiCancelTokenRef.current = null;
        }
    };

    // eslint-disable-next-line
    const generateProcurementSession = async (payload) => {
        try {
            const response = await dispatch(createProcurementSession(payload));
            if (response?.status === 401) {
                handleUnauthorized(navigate, undefined, undefined, SESSION_EXPIRED);
                return null;
            }
            return response;
        } catch (error) {
            console.error("Error generating Procurement Session ID:", error);
            return null;
        }
    };

    const fetchUserConversation = async (user_id, session_id) => {
        setIsChatLoading(true);
        setConverstation([]);
        setUIConversation([]);
        setProcurementTablesData({});
        if (!user_id || !session_id) {
            handleNewSessionContinue();
            return;
        }
        try {
            const params = {
                session_id: session_id,
            };
            const response = await dispatch(fetchProcurementUserConversation(params));

            const messageMap = new Map();
            (response?.messages || []).forEach((message) => {
                const messageId = message.message_id || generateCustomId();
                const existingMessage = messageMap.get(messageId);

                if (!existingMessage) {
                    messageMap.set(messageId, message);
                } else {
                    const messageTime = message.timestamp ? new Date(message.timestamp) : null;
                    const existingTime = existingMessage.timestamp ? new Date(existingMessage.timestamp) : null;

                    if (messageTime && (!existingTime || messageTime > existingTime)) {
                        messageMap.set(messageId, message);
                    } else if (!messageTime && !existingTime && message.summary && !existingMessage.summary) {
                        messageMap.set(messageId, message);
                    }
                }
            });

            const mappedMessages = [];
            const tableDataMap = {};
            const chartDataMap = {};

            Array.from(messageMap.values()).forEach((message) => {
                const messageId = message.message_id || generateCustomId();
                const messageTimestamp = message.timestamp || null;

                // User message - use unique ID to avoid duplicate keys
                const userMessage = {
                    role: "user",
                    content: message.user_query || "",
                    id: `${messageId}-user`,
                    messageId: messageId, // Store original messageId for table data lookup
                    timestamp: convertGMTToIST(messageTimestamp) || new Date().toISOString(),
                };
                mappedMessages.push(userMessage);

                const rawTablesData = message.tables || message.execution_result;
                const parsedExecutionResult = parseTableData(rawTablesData);

                if (parsedExecutionResult && parsedExecutionResult.length > 0) {
                    tableDataMap[messageId] = parsedExecutionResult;
                }

                const rawChartSpecs = message.chart_specs || message.graph || message.charts;
                const parsedChartSpecs = Array.isArray(rawChartSpecs) && rawChartSpecs.length > 0 ? rawChartSpecs : null;
                if (parsedChartSpecs) {
                    chartDataMap[messageId] = parsedChartSpecs;
                }

                let assistantContent = "";
                if (message.summary) {
                    assistantContent = message.summary;
                } else if (parsedExecutionResult && parsedExecutionResult.length > 0) {
                    assistantContent = "Query executed successfully. Please review the results below.";
                } else if (message.error) {
                    assistantContent = `Error: ${message.error}`;
                } else {
                    assistantContent = "No response available.";
                }

                // Assistant message - use unique ID to avoid duplicate keys
                const assistantMessage = {
                    role: "assistant",
                    content: assistantContent,
                    id: `${messageId}-assistant`,
                    messageId: messageId, // Store original messageId for table data lookup
                    feedback: message.feedback || "",
                    timestamp: messageTimestamp,
                    graph: parsedChartSpecs,
                };
                mappedMessages.push(assistantMessage);
            });

            setUIConversation(mappedMessages);
            setConverstation(mappedMessages);
            setProcurementTablesData(tableDataMap);
            setProcurementChartsData(chartDataMap);

            setIsTemplateQueryMode(false);

            setIsChatLoading(false);
            scrollToLatestViewPoint(chatEndRef, isMobile, true);
        } catch (error) {
            setIsChatLoading(false);
            console.error("Error fetching user conversation:", error);
            throw error;
        }
    };

    const handleStopStreaming = () => {
        clearProgressInterval();

        if (chatApiCancelTokenRef.current) {
            cancelCancelToken(chatApiCancelTokenRef.current, "User stopped generation", "procurement chat API");
            chatApiCancelTokenRef.current = null;
        }

        if (templateApiCancelTokenRef.current) {
            cancelCancelToken(templateApiCancelTokenRef.current, "User stopped generation", "template API");
            templateApiCancelTokenRef.current = null;
        }

        if (streamAbortControllerRef.current) {
            try {
                streamAbortControllerRef.current.abort();
            } catch (err) {
                console.warn("AbortController error:", err);
            }
            streamAbortControllerRef.current = null;
        }

        setIsLoading(false);

        const updateLastPlaceholder = prevConvo =>
            prevConvo.map((msg, idx, arr) =>
                idx === arr.length - 1 && (msg.isPlaceholder || msg.isChartLoading)
                    ? {
                        ...msg,
                        isPlaceholder: false,
                        isChartLoading: false,
                        content:
                            msg.content === "Loading..." || msg.content.trim() === ""
                                ? "Streaming Stopped"
                                : msg.content,
                    }
                    : msg
            );

        setUIConversation(updateLastPlaceholder);
        setConverstation(updateLastPlaceholder);
    };

    const handlePromptSelect = (selectedValue) => {
        if (isLoading) return;
        setLastSpendSummaryTimestamp(new Date().toISOString());

        // Find the matching template option from API data
        const matchedOption = templateOptions.find(
            (opt) => opt.template_name.toLowerCase() === selectedValue.toLowerCase()
        );

        // If no sub-options (e.g. "Overall"), call API directly
        const followUpOptions = matchedOption?.options || [];

        if (followUpOptions.length === 0) {
            const query = (matchedOption?.text || "Spend summary for " + selectedValue.toLowerCase());
            handleSendMessage({ query, template: true, templateName: matchedOption?.template_name || selectedValue });
            return;
        }

        setIsTemplateQueryMode(true);

        const messageId = generateCustomId();
        const responseTimestamp = new Date().toISOString();

        const queryText = matchedOption?.text || "Spend summary for " + selectedValue.toLowerCase();

        const userMessage = {
            role: "user",
            content: queryText,
            id: `${messageId}-user`,
            messageId,
            timestamp: responseTimestamp,
        };

        const assistantMessage = {
            role: "assistant",
            content: `Select ${selectedValue} from top ${selectedValue}s <strong>OR</strong> enter any other value manually`,
            id: `${messageId}-assistant`,
            messageId,
            feedback: "",
            timestamp: responseTimestamp,
            isFollowUp: true,
            relevantNames: followUpOptions,
            originalPayload: {
                query: queryText,
                template_name: matchedOption?.template_name || selectedValue,
                user_option: "",
                search_value: "",
            },
        };

        const updatedConvo = [...converstation, userMessage, assistantMessage];
        setConverstation(updatedConvo);
        setUIConversation(updatedConvo);
        scrollToLatestViewPoint(chatEndRef, isMobile, true, false);
    }

    const handleRelevantNameSelect = async (selectedName, originalPayload, messageId) => {
        if (isLoading) return;

        // Mark selected follow-up for this message so we can highlight & disable buttons
        setSelectedFollowUps((prev) => ({
            ...prev,
            [messageId]: selectedName,
        }));

        const newMessageId = generateCustomId();
        const currentTimestamp = new Date().toISOString();

        const selectedUserMessage = {
            role: "user",
            content: originalPayload.query + " " + selectedName,
            id: `${newMessageId}-user`,
            messageId: newMessageId,
            timestamp: currentTimestamp,
        };

        const placeholderBotMessage = {
            role: "assistant",
            content: "Loading...",
            isPlaceholder: true,
            id: `${newMessageId}-assistant`,
            messageId: newMessageId,
            feedback: "",
            timestamp: currentTimestamp,
        };

        // Update UI immediately with user message and placeholder
        const updatedUI = [...converstation, selectedUserMessage, placeholderBotMessage];
        setUIConversation(updatedUI);
        setConverstation(updatedUI);

        scrollToLatestViewPoint(chatEndRef, isMobile, false);
        setIsLoading(true);

        // setIsTemplateQueryMode(true);

        await handleTemplateSendMessage(
            { ...originalPayload, search_value: selectedName },
            newMessageId,
            null,
            [...converstation, selectedUserMessage]
        );
    }

    const handleSendMessage = async ({
        isEdit = false,
        editedConvo = [],
        editedMessage = null,
        messageID = null,
        query = null,
        template = false,
        templateName = "",
    } = {}) => {

        setIsLoading(true);
        if (template) {
            setIsTemplateQueryMode(true);
        }

        const userMessageId = generateCustomId();

        const contentToSend = message?.trim()
            ? message
            : query ? query : "";

        const conversationToCheck = isEdit ? editedConvo : converstation;
        const lastMessage = conversationToCheck[conversationToCheck.length - 1];
        const isManualFollowUpEntry = !isEdit &&
            !template &&
            lastMessage?.isFollowUp &&
            lastMessage?.originalPayload &&
            contentToSend.trim();

        // If manually entering follow-up value, treat it as a follow-up selection
        if (isManualFollowUpEntry) {
            const manualValue = contentToSend.trim();
            const followUpMessageId = lastMessage.messageId;

            // Mark selected follow-up for this message so we can highlight & disable buttons
            setSelectedFollowUps((prev) => ({
                ...prev,
                [followUpMessageId]: manualValue,
            }));

            const newMessageId = generateCustomId();
            const currentTimestamp = new Date().toISOString();

            const selectedUserMessage = {
                role: "user",
                content: lastMessage.originalPayload.query + " " + manualValue,
                id: `${newMessageId}-user`,
                messageId: newMessageId,
                timestamp: currentTimestamp,
            };

            const placeholderBotMessage = {
                role: "assistant",
                content: "Loading...",
                isPlaceholder: true,
                id: `${newMessageId}-assistant`,
                messageId: newMessageId,
                feedback: "",
                timestamp: currentTimestamp,
            };

            // Update UI immediately with user message and placeholder
            const updatedUI = [...converstation, selectedUserMessage, placeholderBotMessage];
            setUIConversation(updatedUI);
            setConverstation(updatedUI);

            scrollToLatestViewPoint(chatEndRef, isMobile, false);
            setIsLoading(true);

            setMessage("");
            if (richInputRef.current) {
                richInputRef.current.clear();
            }

            await handleTemplateSendMessage(
                { ...lastMessage.originalPayload, search_value: manualValue },
                newMessageId,
                null,
                [...converstation, selectedUserMessage]
            );
            return;
        }

        const currentTimestamp = new Date().toISOString();
        const baseMessageId = isEdit ? messageID : userMessageId;

        const userMessage = isEdit
            ? {
                role: "user",
                content: editedMessage?.content || contentToSend,
                id: `${baseMessageId}-user`,
                messageId: baseMessageId, // Store original messageId for consistency
                timestamp: editedMessage?.timestamp || currentTimestamp,
            }
            : {
                role: "user",
                content: contentToSend,
                id: `${baseMessageId}-user`,
                messageId: baseMessageId, // Store original messageId for consistency
                timestamp: currentTimestamp,
            };

        const placeholderBotMessage = {
            role: "assistant",
            content: "Loading...",
            isPlaceholder: true,
            id: `${baseMessageId}-assistant`,
            messageId: baseMessageId, // Store original messageId for consistency
            feedback: "",
            timestamp: currentTimestamp,
        };

        const updatedUI = isEdit
            ? [...editedConvo, userMessage, placeholderBotMessage]
            : [...converstation, userMessage, placeholderBotMessage];
        setUIConversation(updatedUI);

        scrollToLatestViewPoint(chatEndRef, isMobile, false);

        if (!isEdit) {
            setMessage("");
            // Clear the RichInputBox
            if (richInputRef.current) {
                richInputRef.current.clear();
            }
        }

        const messagesForAPI = isEdit
            ? [...editedConvo, userMessage]
            : [...converstation, userMessage];

        const payload = {
            message_id: isEdit ? messageID : userMessageId,
            query: isEdit ? userMessage.content : contentToSend,
            template_name: templateName || "",
            user_option: "",
            search_value: "",
        };
        if (template) {
            await handleTemplateSendMessage(payload, userMessageId, messageID, messagesForAPI);
            return;
        }
        await handleProcurementBotSendMessage(payload, userMessageId, placeholderBotMessage, messageID, isEdit, messagesForAPI);
    };

    const handleAbortMessageError = (messagesForAPI) => {
        const lastMessage = messagesForAPI[messagesForAPI.length - 1];
        const baseMessageId = lastMessage?.messageId || generateCustomId();
        const errorMessage = {
            role: "assistant",
            content: "Sorry, I'm having trouble right now",
            id: `${baseMessageId}-assistant`,
            messageId: baseMessageId,
            feedback: '',
            timestamp: new Date().toISOString(),
        };
        const updatedConvo = [...messagesForAPI, errorMessage];
        setConverstation(updatedConvo);
        setUIConversation(updatedConvo);
        streamReaderRef.current = null;
        setIsLoading(false);
    };

    const stopStreaming = () => {
        clearProgressInterval();
        if (chatApiCancelTokenRef.current) {
            cancelCancelToken(chatApiCancelTokenRef.current, "Streaming stopped", "procurement chat API");
            chatApiCancelTokenRef.current = null;
        }
        if (templateApiCancelTokenRef.current) {
            cancelCancelToken(templateApiCancelTokenRef.current, "Streaming stopped", "template API");
            templateApiCancelTokenRef.current = null;
        }
        if (streamReaderRef.current) {
            streamReaderRef?.current?.cancel();
            streamReaderRef.current = null;
        }

        // Cancel any ongoing file upload
        fileUploadCancelRef.current = true;

        setConverstation([]);
        setUIConversation([]);
    };

    const createFinalBotMessage = (content, messageId, timestamp = null, graph = null, isChartLoading = false) => ({
        role: "assistant",
        content: content || "I apologize, but I couldn't generate a response. Please try again.",
        id: `${messageId}-assistant`,
        messageId: messageId, // Store original messageId for consistency
        feedback: '',
        timestamp: timestamp || new Date().toISOString(),
        graph: graph || null,
        isChartLoading: isChartLoading,
    });

    const checkAndDisplayError = (response, defaultErrorMessage, messageId, messagesForAPI, isEdit) => {
        const errorStatus = response?.status ||
            response?.response?.status ||
            response?.error?.status ||
            response?.response?.statusCode;

        const errorMessage = response?.message ||
            response?.error?.message ||
            response?.response?.data?.message ||
            response?.error?.originalError?.response?.data?.message ||
            (response?.response?.data ? response.response.data.message : null);

        if (errorStatus === 400 || errorStatus === 422 || (errorStatus && errorStatus >= 400) || errorMessage) {
            const displayError = errorMessage || defaultErrorMessage;
            const userMessageTimestamp = messagesForAPI[messagesForAPI.length - 1]?.timestamp || new Date().toISOString();
            const errorBotMessage = createFinalBotMessage(displayError, messageId, userMessageTimestamp);
            const finalConvo = [...messagesForAPI, errorBotMessage];
            setConverstation(finalConvo);
            setUIConversation(finalConvo);
            setIsLoading(false);
            return true;
        }
        return false;
    };

    const pollProcurementChartStatus = async (messageId, sessionId, cancelTokenSource = null) => {
        if (!messageId || !sessionId) return null;

        let lastChartRes = null;

        while (!cancelTokenSource || chatApiCancelTokenRef.current === cancelTokenSource) {
            try {
                const chartRes = await dispatch(
                    getProcurementChartStatus({
                        message_id: messageId,
                        session_id: sessionId,
                    })
                );
                lastChartRes = chartRes;

                const status = chartRes?.status || chartRes?.data?.status;
                const hasSpecs = Array.isArray(chartRes?.chart_specs) && chartRes.chart_specs.length > 0;

                // Stop only when status changes from "processing" (or specs are populated)
                if ((status && status !== "processing") || hasSpecs) {
                    return chartRes;
                }
            } catch (error) {
                console.error("Error fetching procurement chart status:", error);
            }

            await new Promise((resolve) => setTimeout(resolve, 2000));
        }

        return lastChartRes;
    };

    const handleProcurementBotSendMessage = async (payload, userMessageId, placeholderBotMessage, messageID, isEdit, messagesForAPI) => {
        let currentSessionId = sessionManager.getSessionId("procAISessionId");

        const sessionPayload = {
            session_id: currentSessionId || sessionId || "",
            user_query: payload?.query || "",
        };

        const sessionInitResponse = await generateProcurementSession(sessionPayload);
        const resolvedSessionId = sessionInitResponse?.session_id || currentSessionId || sessionId || "";
        const resolvedMessageId = sessionInitResponse?.message_id || (isEdit ? messageID : userMessageId);

        if (resolvedSessionId) {
            setSessionId(resolvedSessionId);
            sessionManager.setSessionId(resolvedSessionId, "procAISessionId");
        }

        // If a new message_id was assigned by sessionInitResponse, update placeholder in convo
        if (sessionInitResponse?.message_id && sessionInitResponse.message_id !== (isEdit ? messageID : userMessageId)) {
            const syncMessageId = (prevConvo) =>
                prevConvo.map((msg, idx, arr) => {
                    if (idx === arr.length - 1 && msg.role === "assistant" && msg.isPlaceholder) {
                        return {
                            ...msg,
                            id: `${resolvedMessageId}-assistant`,
                            messageId: resolvedMessageId,
                        };
                    }
                    return msg;
                });
            setUIConversation(syncMessageId);
            setConverstation(syncMessageId);
        }

        const basePayload = {
            session_id: resolvedSessionId,
            message_id: resolvedMessageId,
            user_query: payload?.query || "",
        };

        clearProgressInterval();

        const pollProgress = async () => {
            if (!resolvedMessageId || !resolvedSessionId) return;

            try {
                const progressRes = await dispatch(
                    getProcurementProgressTracker({
                        message_id: resolvedMessageId,
                        session_id: resolvedSessionId,
                    })
                );

                const rawSteps =
                    progressRes?.steps ||
                    progressRes?.data?.steps ||
                    (Array.isArray(progressRes) ? progressRes : []) ||
                    (Array.isArray(progressRes?.data) ? progressRes.data : []);

                if (Array.isArray(rawSteps) && rawSteps.length > 0) {
                    const formattedSteps = rawSteps.map((step, index) => {
                        const label =
                            typeof step === "string"
                                ? step
                                : step?.status || step?.label || step?.message || step?.step || "";
                        const isLast = index === rawSteps.length - 1;
                        return {
                            label,
                            status: isLast ? "in_progress" : "done",
                            timestamp: step?.timestamp,
                        };
                    });

                    const updateSteps = (prevConvo) =>
                        prevConvo.map((msg, idx, arr) => {
                            if (idx === arr.length - 1 && msg.role === "assistant" && msg.isPlaceholder) {
                                return {
                                    ...msg,
                                    steps: formattedSteps,
                                };
                            }
                            return msg;
                        });

                    setUIConversation(updateSteps);
                    setConverstation(updateSteps);
                }
            } catch (error) {
                console.error("Error fetching procurement progress:", error);
            }
        };

        // Start progress polling interval every 2 seconds
        progressIntervalRef.current = setInterval(pollProgress, 2000);

        cancelCancelToken(chatApiCancelTokenRef.current, "Previous procurement chat cancelled", "previous procurement chat API");
        const cancelTokenSource = axios.CancelToken.source();
        chatApiCancelTokenRef.current = cancelTokenSource;

        try {
            const response = await dispatch(fetchProcurementChatCompletions(basePayload, cancelTokenSource));

            if (axios.isCancel(response) || response?.cancelled) {
                return;
            }

            if (checkAndDisplayError(
                response,
                "An error occurred while processing your request. Please try again.",
                resolvedMessageId,
                messagesForAPI,
                isEdit
            )) {
                return;
            }

            // Persist the session_id returned by the API (important for new chats)
            const returnedSessionId = response?.session_id;
            if (returnedSessionId) {
                setSessionId(returnedSessionId);
                sessionManager.setSessionId(returnedSessionId, "procAISessionId");
            }

            // Use message_id from response if available, fall back to resolved id
            const finalMessageId = response?.message_id || resolvedMessageId;

            // Store table data keyed by message_id
            const parsedTables = parseTableData(response?.tables);
            if (parsedTables && parsedTables.length > 0) {
                setProcurementTablesData((prev) => ({
                    ...prev,
                    [finalMessageId]: parsedTables,
                }));
            }

            const assistantContent = response?.summary || "";

            const userMessageTimestamp = messagesForAPI[messagesForAPI.length - 1]?.timestamp || new Date().toISOString();
            const finalBotMessage = createFinalBotMessage(assistantContent, finalMessageId, userMessageTimestamp, null, true);
            const finalConvo = [...messagesForAPI, finalBotMessage];
            setConverstation(finalConvo);
            setUIConversation(finalConvo);

            if (finalConvo.length <= 2) {
                setTimeout(() => {
                    emit("updateThreads");
                }, 500);
            }

            // Stop progress steps polling now that chat response is generated
            clearProgressInterval();

            // Call chart status and wait for status to change from "processing"
            const chartRes = await pollProcurementChartStatus(
                finalMessageId,
                returnedSessionId || resolvedSessionId,
                cancelTokenSource
            );

            const rawChartSpecs = chartRes?.chart_specs || chartRes?.data?.chart_specs;
            const validChartSpecs = Array.isArray(rawChartSpecs) && rawChartSpecs.length > 0 ? rawChartSpecs : null;

            if (validChartSpecs) {
                setProcurementChartsData((prev) => ({
                    ...prev,
                    [finalMessageId]: validChartSpecs,
                }));
            }

            const updateConvoWithChart = (prevConvo) =>
                prevConvo.map((msg) => {
                    if ((msg.messageId === finalMessageId || msg.id === `${finalMessageId}-assistant`) && msg.role === "assistant") {
                        return {
                            ...msg,
                            isChartLoading: false,
                            graph: validChartSpecs,
                        };
                    }
                    return msg;
                });

            setConverstation(updateConvoWithChart);
            setUIConversation(updateConvoWithChart);

        } catch (error) {
            if (axios.isCancel(error) || error?.cancelled) {
                console.warn("Procurement chat request was cancelled");
                return;
            }
            console.error("Procurement Assistant error:", error);
            checkAndDisplayError(
                error,
                "Sorry, I'm having trouble right now. Please try again.",
                resolvedMessageId,
                messagesForAPI,
                isEdit
            );
        } finally {
            if (chatApiCancelTokenRef.current === cancelTokenSource) {
                chatApiCancelTokenRef.current = null;
            }
            clearProgressInterval();
            setIsLoading(false);
            setIsTemplateQueryMode(false);
            const clearChartLoading = (prevConvo) =>
                prevConvo.map((msg) =>
                    msg.isChartLoading ? { ...msg, isChartLoading: false } : msg
                );
            setConverstation(clearChartLoading);
            setUIConversation(clearChartLoading);
        }
    };

    const handleProcurementTableDownload = (pair) => {
        const originalMessageId = pair?.messageId || (typeof pair === 'object' ? pair?.id?.replace(/-assistant$/, '').replace(/-user$/, '') : pair) || pair?.id || pair;
        emit("downloadProcurementTableData", originalMessageId);
    };

    const handleInputChange = (text) => {
        const inputValue = text;

        const filteredValue = inputValue
            .split('')
            .join('');

        setMessage(filteredValue);
    };

    // Handle prompt selection from ChatPromptBox
    const handleTemplateSendMessage = async (payload, userMessageId, messageID, messagesForAPI) => {
        cancelCancelToken(templateApiCancelTokenRef.current, "Previous template API cancelled", "previous template API");

        const cancelTokenSource = axios.CancelToken.source();
        templateApiCancelTokenRef.current = cancelTokenSource;

        const basePayload = {
            query: payload?.query || "",
            template_name: payload?.template_name || "",
            user_option: payload?.user_option || "",
            search_value: payload?.search_value || "",
        };

        try {
            const templateResponse = await dispatch(fetchProcurementTemplateRequest(basePayload, cancelTokenSource));

            const messageId = messageID || userMessageId;
            const responseTimestamp = new Date().toISOString();

            let assistantMessage;

            const followUp = templateResponse?.follow_up === "true" || templateResponse?.follow_up === true;

            if (followUp) {
                const names = parseRelevantNames(templateResponse?.relevent_names);

                const query = basePayload.query || "";
                const queryParts = query.toLowerCase().split("spend summary for");
                const optionType = queryParts.length > 1
                    ? queryParts[1].trim().split(/\s+/).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ")
                    : "";

                const content = names.length > 0
                    ? `Select ${optionType}`
                    : "No relevant options available. Please try a different query.";

                assistantMessage = {
                    role: "assistant",
                    content: content,
                    id: `${messageId}-assistant`,
                    messageId,
                    feedback: "",
                    timestamp: responseTimestamp,
                    isFollowUp: true,
                    relevantNames: names,
                    originalPayload: basePayload,
                };

                const finalConvo = [...messagesForAPI, assistantMessage];
                setConverstation(finalConvo);
                setUIConversation(finalConvo);
                setIsLoading(false);
                return;
            }

            const templateItems = templateResponse?.template_response;

            if (!Array.isArray(templateItems) || templateItems.length === 0) {
                const fallbackMessage = createFinalBotMessage(
                    "No template response available.",
                    messageId,
                    responseTimestamp
                );
                const finalConvo = [...messagesForAPI, fallbackMessage];
                setConverstation(finalConvo);
                setUIConversation(finalConvo);
                return;
            }

            if (templateResponse?.status === 401) {
                handleUnauthorized(navigate, undefined, undefined, SESSION_EXPIRED);
                return;
            }

            const combinedTables = {};
            const combinedSections = templateItems.map((item, idx) => {
                const entryId = `${messageId}-template-${idx}`;
                const tableData = parseTableData(item?.table);
                const heading = item?.heading?.trim() || "";
                const summary = item?.summary?.trim() || "";

                if (tableData.length > 0) {
                    combinedTables[entryId] = tableData;
                }

                return {
                    heading,
                    summary,
                    tableKey: tableData.length > 0 ? entryId : "",
                    hasContent: heading || summary || (tableData.length > 0),
                };
            }).filter(section => section?.hasContent);

            assistantMessage = {
                role: "assistant",
                content: combinedSections,
                id: `${messageId}-assistant`,
                messageId,
                feedback: "",
                timestamp: responseTimestamp,
                isTemplateBundle: true,
            };

            setProcurementTablesData((prev) => ({ ...prev, ...combinedTables }));

            const finalConvo = [...messagesForAPI, assistantMessage];
            setConverstation(finalConvo);
            setUIConversation(finalConvo);

        } catch (error) {
            if (axios.isCancel(error)) {
                console.error("Template API request was cancelled");
                return;
            }
            console.error("Template request error:", error);
            handleAbortMessageError(messagesForAPI);
        } finally {
            if (templateApiCancelTokenRef.current === cancelTokenSource) {
                templateApiCancelTokenRef.current = null;
            }
            setIsLoading(false);
        }
    }

    const handleEditClick = (index, pair) => {
        if (pair?.role === "user") {
            setEditingId(index);
            setEditedText(uiConversation[index].content);
        }
    };

    const handleSave = async (editIndex, editData) => {
        // Keep only entries with keys < editIndex
        const updatedFeedback = {};
        for (const key in selectedFeedback) {
            if (parseInt(key) < editIndex) {
                updatedFeedback[key] = selectedFeedback[key];
            }
        }
        setSelectedFeedback(updatedFeedback);
        setEditingId(null);
        if (!editedText.trim()) return;
        const editingIndex = converstation.findIndex(
            (msg, index) => index === editIndex && msg.role === "user"
        );
        if (editingIndex === -1) {
            return;
        }
        stopStreaming();
        const originalMessage = converstation[editingIndex];
        const updatedUserMessage = {
            role: "user",
            content: sanitizeTextPreservingHTML(editedText),
            timestamp: originalMessage?.timestamp || new Date().toISOString(),
        };
        const trimmedConvo = converstation.slice(0, editingIndex);
        const originalMessageId = editData?.messageId || editData?.id?.replace(/-user$/, '').replace(/-assistant$/, '') || editData?.id;
        await handleSendMessage({
            isEdit: true,
            editedConvo: trimmedConvo,
            editedMessage: updatedUserMessage,
            messageID: originalMessageId,
        });

        setEditedText("");
    };


    useEffect(() => {
        const unsubscribe = subscribe("thread", async (agentType, sessionId) => {
            if (agentType !== "PROCUREMENT_AI") return;

            if (!sessionId) return;

            setSessionId(sessionId);
            const localSessionId = sessionManager.getSessionId('procAISessionId');
            setEditingId(null);
            if (localSessionId !== sessionId) {
                sessionManager.setSessionId(sessionId, 'procAISessionId');
                fetchUserConversation(username, sessionId);
                stopStreaming();
            }
        });

        return () => unsubscribe();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subscribe]);

    const handleScroll = () => {
        if (chatContainerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
            const isAtBottom = scrollHeight - scrollTop - clientHeight < 100;
            setShowScrollButton(!isAtBottom);
        }
    };

    useEffect(() => {
        const container = chatContainerRef.current;
        if (container) {
            container.addEventListener("scroll", handleScroll);
            return () => container.removeEventListener("scroll", handleScroll);
        }
    }, []);

    useEffect(() => {
        handleScroll();
    }, [uiConversation]);

    const handleNewSessionContinue = async () => {
        stopStreaming();
        clearChatData();
        setIsLoading(false);
        setIsChatLoading(false);
        sessionManager.setSessionId(null, "procAISessionId");
        setSessionId("");
        emit("resetSelectedThread");
    };

    const handleFeedback = async (index, type, data, comment = "") => {
        // Update UI conversation with feedback type
        setUIConversation((prev) =>
            prev.map((msg, idx) =>
                idx === index ? { ...msg, feedback: type } : msg
            )
        );

        // Update converstation so it persists
        setConverstation((prev) =>
            prev.map((msg, idx) =>
                idx === index ? { ...msg, feedback: type } : msg
            )
        );

        // Update feedback selection
        setSelectedFeedback((prev) => ({
            ...prev,
            [index]: type,
        }));

        if (comment) {
            setFeedbackInput((prev) => ({
                ...prev,
                [index]: { comment },
            }));
        }

        await handleFeedbackSubmit(type, index, data, comment);
    };

    const handleFeedbackSubmit = async (type, index, pair, commentParam = "") => {
        if (submittingFeedback) return;

        setSubmittingFeedback(true);

        try {
            const newSessionId = sessionManager.getSessionId("procAISessionId") || sessionId;

            const comment = sanitizeTextInput(
                commentParam !== "" ? commentParam : (feedbackInput?.[index]?.comment?.trim() || "")
            );

            const originalMessageId = pair?.messageId || pair?.id || "";
            const requestBody = {
                session_id: newSessionId,
                message_id: originalMessageId,
                feedback: type ?? "",
                feedback_comment: comment,
                app_name: "proc-ai",
            };

            const feedbackAction = updateProcurementFeedback;

            const response = await dispatch(feedbackAction(requestBody)).unwrap?.();

            if (response) {
                toast.info(FEEDBACK_SUBMIT_SUCCESS || "Feedback submitted successfully");
                setSelectedFeedback((prev) => ({ ...prev, [index]: type }));
            }

            setFeedbackInput((prev) => ({
                ...prev,
                [index]: { comment: "" },
            }));
        } catch (error) {
            console.error("Feedback submit error:", error);
            toast.error("Failed to submit feedback");
        } finally {
            setSubmittingFeedback(false);
        }
    };

    useEffect(() => {
        // Only disable the textbox when loading, not when empty
        setIsTextBoxDisabled(isLoading);
    }, [isLoading]);

    useEffect(() => {
        // Disable send button when no message and no files, or when loading
        const hasMessage = !!message.trim();
        setIsSendButtonDisabled(!(hasMessage) || isLoading);
    }, [message, isLoading]);

    // Function to group messages with date separators
    const groupMessagesWithDateSeparators = (messages) => {
        if (!messages || messages.length === 0) return [];

        const grouped = [];
        let lastDateKey = null;

        messages.forEach((message, index) => {
            const messageTimestamp = message.timestamp;
            const dateKey = messageTimestamp ? getDateKey(messageTimestamp) : null;

            // Add date separator if date changed
            if (dateKey && dateKey !== lastDateKey) {
                const dateLabel = formatMessageDate(messageTimestamp);
                grouped.push({
                    type: 'date-separator',
                    dateLabel: dateLabel,
                    dateKey: dateKey,
                    id: `date-sep-${dateKey}-${index}`,
                });
                lastDateKey = dateKey;
            }

            grouped.push(message);
        });

        return grouped;
    };

    // Determine if chat is in a fresh state (no conversation yet)
    const isNewChat = (uiConversation?.length ?? 0) === 0;
    const shouldShowSpendSummaryPrompts = !isChatLoading && (isNewChat || isTemplateQueryMode);

    const renderHeading = (heading) => {
        if (!heading?.trim()) return null;
        return <div className="heading-text">{heading}</div>;
    };

    const renderTable = (messageId, showQueryResultLabel = false) => {
        const hasTableData = procurementTablesData?.[messageId]?.length > 0;
        if (!hasTableData) return null;

        return (
            <div className="procurement-table-section">
                {showQueryResultLabel && <span className="heading-text">{QUERY_RESULT}</span>}
                <div className="procurement-table-data-section">
                    <ProcurementDynamicTable data={procurementTablesData[messageId]} messageId={messageId} />
                </div>
            </div>
        );
    };

    const normalizeSummaryContent = (content) => content
            .replace(/\\n/g, '\n')
            .replace(/\/n/g, '\n');

    const getSummaryHeadingIndex = (content) => (
        normalizeSummaryContent(content).split('\n').findIndex((line) => (
            line
                .replace(/^#{1,6}\s*/, '')
                .replace(/\*\*|__/g, '')
                .replace(/:$/, '')
                .trim()
                .toLowerCase() === 'summary'
        ))
    );

    const renderSummary = (content, refIndex, graphSection = null) => {
        if (typeof content !== 'string' || !content?.trim()) return graphSection;

        const normalizedContent = normalizeSummaryContent(content);
        const lines = normalizedContent.split('\n');
        const summaryHeadingIndex = getSummaryHeadingIndex(content);
        const formatMarkdown = (value) => value.replace(/\n/g, '  \n');

        return (
            <div ref={(el) => (assistantRefs.current[refIndex] = el)}>
                {graphSection && summaryHeadingIndex >= 0 ? (
                    <>
                        <MarkdownContent
                            content={formatMarkdown(lines.slice(0, summaryHeadingIndex + 1).join('\n'))}
                            useMathJax={false}
                        />
                        {graphSection}
                        {lines.slice(summaryHeadingIndex + 1).join('\n').trim() && (
                            <MarkdownContent
                                content={formatMarkdown(lines.slice(summaryHeadingIndex + 1).join('\n'))}
                                useMathJax={false}
                            />
                        )}
                    </>
                ) : (
                    <>
                        {graphSection}
                        <MarkdownContent content={formatMarkdown(normalizedContent)} useMathJax={false} />
                    </>
                )}
            </div>
        );
    };

    const renderTemplateSection = (section, sectionIdx, messageId, refIndex) => {
        const hasHeading = !!section?.heading;
        const hasSummary = !!section?.summary;
        const tableKey = section?.tableKey;
        const hasTable = tableKey && procurementTablesData?.[tableKey]?.length > 0;

        if (!hasHeading && !hasSummary && !hasTable) return null;

        return (
            <div
                key={`${messageId}-section-${sectionIdx}`}
                className={`template-section ${sectionIdx > 0 ? 'template-section-spaced' : ''}`}
            >
                {renderHeading(section.heading)}
                {hasTable && (
                    <div className="procurement-table-section">
                        <div className="procurement-table-data-section">
                            <ProcurementDynamicTable data={procurementTablesData[tableKey]} messageId={tableKey} />
                        </div>
                    </div>
                )}
                {renderSummary(section.summary, refIndex)}
            </div>
        );
    };

    return (
        <>
            <div className="proc-page-wrapper">
                <ChatHeader
                    title={`${YOU_ARE_CHATTING_WITH} ${SHRADDHA_AI || SHARADHA_AI || "Shraddha AI"}`}
                    backBtnText={BACK_TO_CHAT_GENIE}
                    onBack={routeToUPLGenieChat}
                />

                <div className="proc-container">
                    {/* Scrollable chat body */}
                    <div className="proc-chat-body" ref={chatContainerRef}>
                        <div className="proc-content-column">
                            <LoadingScreen isLoading={isChatLoading} />
                            {!isChatLoading && (uiConversation?.length ?? 0) === 0 ? (
                                <div className="proc-new-chat-content">
                                    {!(isMobile && isTextareaFocused) && (
                                        <Dashboard
                                            title="Direct Procurement Spend Intelligence"
                                            cardsData={procurementCardsData}
                                            onPromptClick={(prompt) => {
                                                handleInputChange(prompt);
                                            }}
                                        />
                                    )}
                                </div>
                            ) : (
                                <div className="proc-messages-list">
                                    {(() => {
                                        const groupedMessages = groupMessagesWithDateSeparators(uiConversation || []);
                                        let actualMessageIndex = -1;
                                        return groupedMessages.map((pair, groupedIndex) => {
                                            if (pair.type !== 'date-separator') {
                                                actualMessageIndex++;
                                            }
                                            const actualIndex = actualMessageIndex;
                                            const isLastMessage = actualIndex === (uiConversation?.length ?? 0) - 1;
                                            const graphDataForCopy =
                                                pair?.graph || procurementChartsData?.[pair?.messageId || pair?.id];
                                            const graphTableDataForCopy =
                                                procurementTablesData?.[pair?.messageId || pair?.id];
                                            const graphActions = [
                                                "copy",
                                                {
                                                    item: "table-copy",
                                                    data: graphDataForCopy,
                                                    tableData: graphTableDataForCopy,
                                                },
                                                "download",
                                            ];
                                            const formattedGraph = formatProcurementGraphSnapshot(
                                                graphDataForCopy,
                                                isMobile
                                            );
                                            const renderGraphInSummary =
                                                pair?.role === "assistant" &&
                                                !pair?.isTemplateBundle &&
                                                !pair?.isFollowUp &&
                                                typeof pair?.content === "string" &&
                                                getSummaryHeadingIndex(pair.content) >= 0;

                                            return (
                                                <span key={pair.id || groupedIndex}>
                                                    {pair?.type === "date-separator" && (
                                                        <div className="date-separator-container">
                                                            <div className="date-separator">
                                                                {pair?.dateLabel || ""}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {pair.type !== 'date-separator' && (
                                                        <MessageBox
                                                            key={pair.id || actualIndex}
                                                            pair={{
                                                                ...pair,
                                                                graph: renderGraphInSummary ? null : formattedGraph,
                                                                summaryGraph: renderGraphInSummary ? formattedGraph : null,
                                                            }}
                                                            index={actualIndex}
                                                            editingId={editingId}
                                                            editedText={editedText}
                                                            setEditedText={setEditedText}
                                                            isMobile={isMobile}
                                                            fullname={fullname}
                                                            isTextBoxDisabled={isTextBoxDisabled}
                                                            showEditPrompt={false}
                                                            botIcon={procurementBotIcon}
                                                            isLoading={isLoading}
                                                            isLastMessage={isLastMessage}
                                                            handleEditClick={handleEditClick}
                                                            handleSave={handleSave}
                                                            setEditingId={setEditingId}
                                                            showTimeStamp={pair.role === "user"}
                                                            formatMessageTime={formatMessageTime}
                                                            showFeedback={false}
                                                            useMathJax={false}
                                                            showProgressSteps={pair.role === "assistant" && pair.steps?.length > 0}
                                                            renderContent={(p, idx) => {
                                                                if (p.role !== "assistant") return null;
                                                                if (p.content === "Loading...") {
                                                                    if (p.steps && p.steps.length > 0) {
                                                                        return null;
                                                                    }
                                                                    return (
                                                                        <div className="loader-section procurement-section">
                                                                            <div className="loader-inner-section">
                                                                                <div className="spinner-small"></div>
                                                                                <span className="loading-text">{UNDERSTANDING_THE_QUERY}</span>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                }
                                                                const summaryGraphSection = p?.summaryGraph ? (
                                                                    <MessageGraphs
                                                                        graph={p.summaryGraph}
                                                                        showGraphActions={graphActions}
                                                                        onGraphActionStatus={handleGraphActionStatus}
                                                                    />
                                                                ) : null;
                                                                return (
                                                                    <div className="math-jsx">
                                                                        {p?.isTemplateBundle ? (
                                                                            (Array.isArray(p.content) ? p.content : []).map((section, sectionIdx) =>
                                                                                renderTemplateSection(section, sectionIdx, p.messageId, idx)
                                                                            )
                                                                        ) : p?.isFollowUp ? (
                                                                            <>
                                                                                {renderSummary(p?.content, idx)}
                                                                                {p?.relevantNames && p.relevantNames.length > 0 && (
                                                                                    <div className="relevant-names-container">
                                                                                        <div className="relevant-names-buttons">
                                                                                            {p.relevantNames.map((name, nameIdx) => {
                                                                                                const selectedForMessage = selectedFollowUps[p.messageId];
                                                                                                const isSelected = selectedForMessage === name;
                                                                                                const hasSelection = !!selectedForMessage;
                                                                                                const isDisabledByNewSummary = lastSpendSummaryTimestamp &&
                                                                                                    p.timestamp &&
                                                                                                    new Date(p.timestamp) < new Date(lastSpendSummaryTimestamp);
                                                                                                const isDisabled = hasSelection || isDisabledByNewSummary;
                                                                                                return (
                                                                                                    <button
                                                                                                        key={nameIdx}
                                                                                                        type="button"
                                                                                                        className={`relevant-name-btn${isSelected ? " relevant-name-btn-selected" : ""}`}
                                                                                                        onClick={() => {
                                                                                                            if (!isDisabled) {
                                                                                                                handleRelevantNameSelect(name, p.originalPayload, p.messageId);
                                                                                                            }
                                                                                                        }}
                                                                                                        disabled={isDisabled}
                                                                                                    >
                                                                                                        {name}
                                                                                                    </button>
                                                                                                );
                                                                                            })}
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                {renderHeading(p?.heading)}
                                                                                {p?.content !== "Loading..." && p?.role === "assistant" && (
                                                                                    renderTable(p?.messageId || p?.id, true)
                                                                                )}
                                                                                {renderSummary(p?.content, idx, summaryGraphSection)}
                                                                                {p?.isChartLoading && (
                                                                                    <div className="loader-section procurement-section procurement-chart-loader" style={{ marginTop: "16px" }}>
                                                                                        <div className="loader-inner-section">
                                                                                            <div className="spinner-small"></div>
                                                                                            <span className="loading-text">Generating chart...</span>
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                );
                                                            }}
                                                            renderExtras={(p, idx) => {
                                                                if (p.role !== "assistant" || p.content === "Loading..." || p?.isFollowUp) return null;
                                                                return (
                                                                    <FeedbackButtons
                                                                        show={["like", "dislike", "copy"]}
                                                                        pair={p}
                                                                        index={idx}
                                                                        getTextElement={() => assistantRefs.current[idx]}
                                                                        pairId={p?.messageId || p?.id}
                                                                        feedback={p.feedback}
                                                                        isLoading={isLoading}
                                                                        isLastMessage={isLastMessage}
                                                                        onFeedback={handleFeedback}
                                                                        onDownload={handleProcurementTableDownload}
                                                                        procurementTableData={procurementTablesData?.[p?.messageId || p?.id]}
                                                                        currentRole={{ name: PROCUREMENT_AI }}
                                                                    />
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                </span>
                                            );
                                        });
                                    })()}
                                </div>
                            )}
                            <div ref={chatEndRef} />
                        </div>
                    </div>

                    {/* Sticky footer — always at bottom */}
                    {!isChatLoading && (
                        <div className="proc-footer">
                            <div className="proc-content-column">
                                {/* Scroll-to-bottom button */}
                                {showScrollButton && (
                                    <div className="proc-scroll-btn-row">
                                        <button
                                            className="proc-scroll-bottom-btn"
                                            onClick={() => scrollToLatestViewPoint(chatEndRef, isMobile, true, true)}
                                        >
                                            <img src={downArrow} alt="Scroll to bottom" />
                                        </button>
                                    </div>
                                )}

                                {shouldShowSpendSummaryPrompts && spendSummaryPrompts.length > 0 && (
                                    <div className="procurement-above-cpc">
                                        <span className="procurement-prompt-label">Provide me a spend summary:</span>
                                        <div className="procurement-prompt-pills">
                                            {spendSummaryPrompts.map((prompt, idx) => (
                                                <button
                                                    key={idx}
                                                    type="button"
                                                    className={`procurement-pill-btn ${isLoading ? "disabled" : ""}`}
                                                    onClick={() => !isLoading && handlePromptSelect(prompt)}
                                                    disabled={isLoading}
                                                >
                                                    {prompt}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <ChatPromptContainer
                                    value={message}
                                    onChange={handleInputChange}
                                    isLoading={isLoading}
                                    placeholder="Ask me anything about procurement..."
                                    handleSendMessage={handleSendMessage}
                                    handleStopStreaming={handleStopStreaming}
                                    richInputRef={richInputRef}
                                    selectedFiles={[]}
                                    setSelectedFiles={() => { }}
                                    suggestivePrompts={[]}
                                    activeCategory={null}
                                    onSuggestivePromptClick={handlePromptSelect}
                                    setIsTextareaFocused={setIsTextareaFocused}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

export default ProcurementChat;